import { ClientSociety, ClientUser } from "@dune/types";
import { Button, Input } from "@dune/ui";
import { Plus, SearchSm } from "@untitled-ui/icons-react";
import { useState } from "react";
import SelectClientCard from "./SelectClientCard";

import { useDebounce } from "use-debounce";

import { useFormContext } from "react-hook-form";
import { defaultClientSocietyValues } from "@dune/vars";
import { useGetClients } from "../../../api/queries/constructionSite.queries";
import { formatClientSocietyForForm } from "../../../helpers/newClientForm.helper";
import { isClientUser } from "../../../utils/clients";
import { SelectedClient } from "../../../pages/constructionSites/projectForm/CommonProjectForm";

type SelectOrCreateClientContainerProps = {
	setSelectedClient: (client: SelectedClient) => void;
	excludeSyndics: boolean;
};

const SelectOrCreateClientContainer = ({
	setSelectedClient,
	excludeSyndics,
}: SelectOrCreateClientContainerProps) => {
	const [searchInput, setSearchInput] = useState("");
	const [debouncedSearchInput] = useDebounce(searchInput, 500);
	const { data: clients } = useGetClients(debouncedSearchInput, excludeSyndics);
	const { setValue } = useFormContext();

	const handleCreateNewClient = () => {
		setSelectedClient(null);
		setValue("clientType", "SOCIETY");
		setValue("clientSocietyId", undefined);
		setValue("clientSociety", {
			...defaultClientSocietyValues,
			id: undefined,
		});
		setValue("clientSociety.siren", "");
		setValue("clientSociety.denomination", "");
		setValue("clientSociety.streetNumber", "");
		setValue("clientSociety.streetName", "");
		setValue("clientSociety.postalCode", "");
		setValue("clientSociety.city", "");
	};
	return (
		<div className="flex flex-col gap-6 w-full">
			<h1 className="text-[#144C65] text-2xl font-medium">Mon client</h1>
			<div className="flex flex-col border-solid border-1 border-gray-200 rounded-lg p-6 gap-4">
				Rechercher
				<div className="flex items-center gap-4">
					<Input
						type="text"
						placeholder="SIREN, Nom, Prénom, Raison sociale..."
						onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
							setSearchInput(e.target.value)
						}
						iconOrText={<SearchSm />}
						iconOrTextPosition="right"
					/>
					ou
					<Button
						type="button"
						onClick={handleCreateNewClient}
						className="bg-[#038EB7]"
						size="lg"
					>
						<Plus />
						Créer un nouveau client
					</Button>
				</div>
			</div>
			<div className="flex flex-col rounded-lg gap-4">
				{debouncedSearchInput && (
					<h2 className="text-[#144C65] text-[18px] font-medium">
						{`${clients?.length || 0} client${clients?.length > 1 ? "s" : ""} trouvé${clients?.length > 1 ? "s" : ""}`}
					</h2>
				)}
				<div className="grid grid-cols-2 gap-4">
					{clients?.map((client: ClientUser | ClientSociety) => {
						return isClientUser(client) ? (
							<SelectClientCard
								key={client.email}
								client={client}
								onSelect={(client: ClientUser) => {
									setSelectedClient([client]);
									setValue("clientType", "USER");
									setValue("clientUsers", [
										{
											...client,
											firstname: client.firstname ?? "",
											lastname: client.lastname ?? "",
											email: client.email ?? "",
											streetNumber: client.address?.streetNumber ?? "",
											streetName: client.address?.streetName ?? "",
											postalCode: client.address?.postalCode ?? "",
											city: client.address?.city ?? "",
										},
									]);
								}}
							/>
						) : (
							<SelectClientCard
								key={client.siren}
								client={client}
								onSelect={(client: ClientSociety) => {
									setSelectedClient(client);
									setValue("clientType", "SOCIETY");
									setValue("clientSocietyId", client.id);
									setValue("clientSociety", formatClientSocietyForForm(client));
								}}
							/>
						);
					})}
				</div>
			</div>
		</div>
	);
};

export default SelectOrCreateClientContainer;
